import { useSelector } from 'react-redux';
import { DateFormat, formatDate } from 'susy-common/date-utils';
import { RootState } from '../../store';
import IconTextGroup from '../shared/iconTextGroup/IconTextGroup';
import SusyAvatar, { SuSyAvatarSizes } from '../shared/susyAvatar/SusyAvatar';
import { ITopicDisplay, ITopicDisplayParticipant } from './interfaces';

import './SearchResultsItem.css';
import SusyHighlighter from './SusyHighlighter';

interface SearchResultsItemProps {
    topic: ITopicDisplay;
    projectName: string;
    searchTerm: string;
    relevantMessage?: string;
    color: string;
    enterTopicCallback: (topic: ITopicDisplay) => void;
}

const SearchResultsItem = ({
    topic,
    projectName,
    color,
    searchTerm,
    relevantMessage,
    enterTopicCallback,
}: SearchResultsItemProps) => {
    const currentUser = useSelector(
        (state: RootState) => state.auth.currentUser
    );

    const renderParticipantsAvatars = (
        participants: ITopicDisplayParticipant[],
        shouldExcludeCurrentUser = false
    ) => {
        if (!participants) {
            return null;
        }

        const participantsForDisplaying = shouldExcludeCurrentUser
            ? participants.filter((p) => p.userId !== currentUser?._id)
            : participants;

        const participantPictures = participantsForDisplaying.map((p) => (
            <SusyAvatar
                key={p.profilePictureId}
                profilePictureId={p.profilePictureId}
                size={SuSyAvatarSizes.SM}
            />
        ));

        const iconTextGroup = (
            <IconTextGroup
                avatarsOrIcons={participantPictures}
                text={''}
                classes='susy-project-topic-participant-avatar'
                isClickable={false}
            />
        );

        return iconTextGroup;
    };

    return (
        <div
            className='search-results-item'
            data-testid='search-results-item'
            onClick={() => enterTopicCallback(topic)}
        >
            <div className='susy-topic-sidebar-participants-avatar-list'>
                {renderParticipantsAvatars(topic.participants, true)}
            </div>
            <div className='flex-fill truncating-parent'>
                <div className='d-flex justify-content-between'>
                    <div className='topic-title'>
                        <SusyHighlighter
                            searchTerm={searchTerm}
                            textToHighlight={topic.title}
                        />
                    </div>
                    <div className='susy-topic-date ml-3 text-right'>
                        {formatDate(
                            topic.latestMessage?.time ?? topic.creationDate,
                            DateFormat.MMM_D
                        )}
                    </div>
                </div>
                <div className='d-flex align-items-center'>
                    <div
                        className='susy-topic-sidebar-project-box'
                        style={{
                            background: color,
                        }}
                    ></div>
                    <div className='project-name'>
                        <SusyHighlighter
                            searchTerm={searchTerm}
                            textToHighlight={projectName}
                        />
                    </div>
                </div>
                <div className='relevant-text'>
                    <SusyHighlighter
                        searchTerm={searchTerm}
                        textToHighlight={relevantMessage || ''}
                    />
                </div>
            </div>
        </div>
    );
};

export default SearchResultsItem;
